/* ============================================================
   DailyLama — Interpréteur de rêves
   Fichier destiné à assets/dream-analyzer.js (thème Shopify).

   Règles tenues par ce fichier :
   - aucune clé, aucun secret : toute la config vient du Liquid
     via <script type="application/json" id="dl-dream-config">
   - toute sortie IA est insérée via textContent, jamais innerHTML
   - le texte du rêve ne passe jamais par une URL
   - zéro dépendance externe
   ============================================================ */
(function () {
  'use strict';

  /* Garde de ré-entrée.
     Le snippet dream-analyzer-cta.liquid émet lui-même sa balise <script>. Comme il est
     rendu deux fois par article (CTA après l'intro + CTA de fin), ce fichier s'exécutait
     deux fois : deux écouteurs de clic et deux IntersectionObserver sur chaque bloc, donc
     chaque clic et chaque vue comptés double dans les statistiques.
     Repéré le 26/08/2026 : tous les articles suivis avaient un nombre PAIR de clics. */
  /* Hors navigateur (tests), il n'y a ni `window` ni double montage possible. */
  if (typeof window !== 'undefined' && window.DreamAnalyzer) return;

  var STORE_KEY = 'dl_dream_session';
  var DEFAULTS = {
    apiBase: '', variantAnalysis: null, variantEbook: null, enableEbookBump: false,
    resultPath: '/pages/resultat-reve', toolPath: '/pages/interpreteur-de-reves',
    checkoutUrl: '/checkout', minChars: 80, maxChars: 4000,
    pollIntervalMs: 3000, pollTimeoutMs: 120000, enabled: true
  };

  /* ---------- utilitaires ---------- */

  function cfg(root) {
    var node = root.querySelector('[data-dream-config]') || document.getElementById('dl-dream-config');
    var parsed = {};
    if (node) { try { parsed = JSON.parse(node.textContent); } catch (e) { parsed = {}; } }
    var out = {};
    Object.keys(DEFAULTS).forEach(function (k) { out[k] = k in parsed ? parsed[k] : DEFAULTS[k]; });
    Object.keys(parsed).forEach(function (k) { if (!(k in out)) out[k] = parsed[k]; });
    return out;
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;   // jamais innerHTML sur du contenu généré
    return n;
  }

  function show(node) { if (node) node.classList.remove('dl-hidden'); }
  function hide(node) { if (node) node.classList.add('dl-hidden'); }
  function clear(node) { while (node && node.firstChild) node.removeChild(node.firstChild); }

  function store(data) {
    try {
      if (data === null) localStorage.removeItem(STORE_KEY);
      else localStorage.setItem(STORE_KEY, JSON.stringify(data));
    } catch (e) { /* navigation privée : on continue sans reprise */ }
  }
  function restore() {
    try { return JSON.parse(localStorage.getItem(STORE_KEY) || 'null'); } catch (e) { return null; }
  }

  /* ---------- tracking ---------- */

  function track(config, name, props) {
    var payload = Object.assign({ event: name }, props || {});
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    } catch (e) {}
    try { document.dispatchEvent(new CustomEvent('dl:dream', { detail: payload })); } catch (e) {}
    if (!config || !config.apiBase) return;
    // miroir serveur, best effort : jamais bloquant, jamais de contenu de rêve
    try {
      var body = JSON.stringify({ name: name, props: props || {} });
      // Pas de sendBeacon : un corps « application/json » vers une autre origine impose
      // une requête préalable CORS, que sendBeacon ne sait pas faire. La requête est
      // alors abandonnée sans erreur, et le suivi disparaît en silence.
      // fetch + keepalive survit aussi à la fermeture de l'onglet, sans ce défaut.
      fetch(config.apiBase + '/events', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: body, keepalive: true, mode: 'cors'
      }).catch(function () {});
    } catch (e) {}
  }

  /* ---------- rendu ---------- */

  function renderFree(target, free) {
    clear(target);
    /* Un bloc = un intitulé en petites capitales + son contenu (maquette du 03/09/2026).
       L'intitulé est un <h3> : la lecture gratuite garde une structure de titres. */
    function bloc(titre) {
      var b = el('div', 'dl-summary');
      b.appendChild(el('h3', 'dl-res-label', titre));
      target.appendChild(b);
      return b;
    }
    if (free.dream_summary) bloc('Ce que raconte votre récit').appendChild(el('p', 'dl-res-texte', free.dream_summary));
    if (free.symbols && free.symbols.length) {
      var grid = el('div', 'dl-symbols');
      free.symbols.forEach(function (s) {
        var c = el('div', 'dl-symbol');
        c.appendChild(el('b', null, s.name));
        c.appendChild(el('span', null, s.short_meaning));
        grid.appendChild(c);
      });
      bloc('Symboles détectés').appendChild(grid);
    }
    if (free.dominant_theme) bloc('Thème dominant').appendChild(el('div', 'dl-theme', free.dominant_theme));
    if (free.short_interpretation) bloc('Lecture courte').appendChild(el('p', 'dl-res-texte', free.short_interpretation));
  }

  /* Le paywall n'annonce plus « il y a davantage de contenu » : il affiche les questions
     précises auxquelles l'analyse payante répondra, tirées du récit de la personne.
     Repli sur l'ancien champ paywall_teaser pour les analyses produites avant ce changement. */
  /* Inventaire de l'analyse payante, construit sur les données du rêve réellement
     analysé. Aucun chiffre n'est écrit à la main : le nombre de symboles et de
     questions vient de la lecture gratuite, et les lectures dépendent de l'angle
     demandé. C'est ce qui distingue une promesse d'une description. */
  /* Ordre sur mobile (décision du 08/09/2026) : la lecture gratuite ENTIÈRE,
     puis l'illustration et son déverrouillage par e-mail, puis l'offre payante.
     -------------------------------------------------------------------------
     Au-dessus de 990 px la grille a deux colonnes : le paywall est en colonne
     de droite, visible dès l'affichage du résultat. En dessous, une seule
     colonne : l'ordre est celui du DOM — lecture, illustration, champ e-mail,
     offre, carte pierre.

     Jusqu'au 08/09, `remonterOffre` déplaçait l'offre juste après le premier
     bloc de la lecture sous 990 px (mesure du 03/09 : 17 paywalls vus pour 104
     analyses, 3,47 écrans à défiler). L'offre coupait alors la lecture en deux
     et passait avant l'illustration et son déverrouillage : elle n'est plus
     remontée. La fonction est conservée, inactive, avec sa mesure ; le
     déclencheur de `dream_paywall_seen` (observateur d'intersection à 50 %)
     ne change pas, l'avant-après reste comparable. */
  /* Premier écran après la génération : le début de la lecture gratuite, en haut de l'écran,
     et le focus posé dessus (section focalisable, sans défilement second) pour que lecteurs
     d'écran et clavier partent du bon endroit. La marge de défilement est dans la feuille. */
  function allerAuResultat(section) {
    if (!section) return;
    /* L'en-tête du thème est collant (141 px sur mobile) : sans marge, il recouvre le titre du
       résultat. La marge est prise sur la hauteur réelle de l'en-tête au moment du défilement. */
    try {
      var entete = document.querySelector('header');
      var pos = entete ? getComputedStyle(entete).position : '';
      var haut = entete && (pos === 'sticky' || pos === 'fixed') ? entete.getBoundingClientRect().height : 0;
      section.style.scrollMarginTop = (Math.round(haut) + 12) + 'px';
    } catch (e) {}
    try { section.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) { section.scrollIntoView(); }
    try { section.focus({ preventScroll: true }); } catch (e) {}
    /* Le défilement doux est parfois ignoré juste après un chargement (constaté au rechargement
       sur mobile) : si le résultat n'est pas arrivé en haut, on y va sans animation. */
    setTimeout(function () {
      try {
        var r = section.getBoundingClientRect();
        var marge = parseInt(section.style.scrollMarginTop || '0', 10) || 0;
        if (Math.abs(r.top - marge) > 40) section.scrollIntoView({ behavior: 'auto', block: 'start' });
      } catch (e) {}
    }, 800);
  }

  function remonterOffre(paywall) {
    if (!paywall || paywall.dataset.dlRemonte === 'oui') return;
    if (window.matchMedia && window.matchMedia('(min-width: 990px)').matches) return;
    var libre = document.querySelector('[data-dream-free]');
    if (!libre || !libre.firstElementChild) return;
    /* Après le premier bloc rendu, pas après le premier nœud : la lecture est
       faite de sections, et couper à l'intérieur de l'une d'elles serait pire
       que de laisser l'offre en bas. */
    var premier = libre.children.length > 1 ? libre.children[0] : null;
    if (!premier) return;
    premier.insertAdjacentElement('afterend', paywall);
    paywall.dataset.dlRemonte = 'oui';
  }

  /* « Rendu » n'est pas « vu ». Sur mobile, le paywall peut rester sous la ligne de
     flottaison pendant que le lecteur parcourt sa lecture gratuite puis abandonne :
     l'ancien compteur l'aurait pourtant enregistré comme une offre présentée. */
  function observerPaywall(config, paywall, analysisId) {
    if (!paywall || paywall.dataset.dlVu === 'oui') return;
    if (!('IntersectionObserver' in window)) return;   // pas de mesure inventée
    /* Depuis le 08/09/2026 : ce qui est observé, c'est le bouton de déblocage (prix juste
       au-dessus), entièrement à l'écran — pas la colonne entière à 50 %. Sur mobile, la colonne
       dépasse deux hauteurs d'écran : la moitié n'y était jamais visible d'un coup, et l'offre
       pouvait être lue sans jamais compter. « Offre vue » = l'offre et son prix sous les yeux. */
    var cible = paywall.querySelector('[data-dream-unlock]') || paywall;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        paywall.dataset.dlVu = 'oui';
        io.disconnect();
        track(config, 'dream_paywall_seen', { analysis_id: analysisId });
      });
    }, { threshold: cible === paywall ? 0.5 : 1 });
    io.observe(cible);
  }

  /* « Analyser un autre rêve » n'était mesuré nulle part : on ignore s'il est vu et s'il
     est cliqué. Le tunnel visé est « analyse payée → bouton vu → clic », donc on n'arme
     la mesure QU'APRÈS le rendu de l'analyse : le lien existe dans le gabarit dès le
     chargement, l'observer au départ compterait une exposition sur une page dont
     l'analyse n'est jamais arrivée.

     Même exigence que pour le paywall : un rendu DOM ne vaut pas une exposition, on
     attend que la moitié du bouton entre dans le viewport.

     Le comportement du lien ne change pas : aucun preventDefault, aucune navigation
     retardée. Si le suivi échoue, le clic part quand même. */
  function suivreAutreReve(root, config, analysisId) {
    var lien = root.querySelector('[data-dream-again]');
    if (!lien) return;
    /* Un seul armement, quoi qu'il arrive : le sondage peut rappeler cette fonction,
       et deux écouteurs de clic enverraient deux événements pour un seul clic. */
    if (lien.getAttribute('data-dl-suivi') === '1') return;
    lien.setAttribute('data-dl-suivi', '1');

    lien.addEventListener('click', function () {
      track(config, 'dream_another_dream_click', { analysis_id: analysisId, device: appareil() });
    });

    if (!('IntersectionObserver' in window)) return;   // pas de mesure inventée
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        io.disconnect();          // un retour dans le viewport ne recompte pas
        track(config, 'dream_another_dream_seen', { analysis_id: analysisId, device: appareil() });
      });
    }, { threshold: 0.5 });
    io.observe(lien);
  }

  /* Deux valeurs seulement, dérivées de la largeur : aucune empreinte de navigateur. */
  function appareil() {
    return window.matchMedia && window.matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop';
  }

  function renderInventaire(q, free, angle, config) {
    var host = q('[data-dream-inventaire]');
    if (!host) return;
    var symboles = (free.symbols || []).filter(function (x) { return x && x.name; });
    var questions = (free.locked_questions || []).filter(function (x) { return x && x.title; });

    var lignes = [];
    if (symboles.length) {
      lignes.push([String(symboles.length),
        symboles.length > 1 ? ' symboles, chacun replacé dans votre récit'
                            : ' symbole, replacé dans votre récit']);
    }
    if (questions.length) {
      lignes.push([String(questions.length),
        questions.length > 1 ? ' réponses approfondies aux questions restées ouvertes'
                             : ' réponse approfondie à la question restée ouverte']);
    }
    /* Les libellés suivent l'angle demandé, comme le contenu produit : annoncer
       « émotionnelle et spirituelle » sur un angle Psy décrivait un autre produit. */
    var LECTURES = {
      psychologique: ' lectures : psychologique et contextuelle',
      symbolique: ' lectures : émotionnelle et symbolique',
      spirituelle: ' lectures : émotionnelle et spirituelle',
      islamique: ' lectures : émotionnelle, contextuelle et islamique'
    };
    lignes.push([angle === 'islamique' ? '3' : '2',
                 LECTURES[angle] || LECTURES.symbolique]);
    lignes.push([null, 'Ce que votre rêve ne signifie probablement pas']);
    lignes.push(['3', ' pistes de réflexion ancrées dans votre récit']);
    lignes.push([null, 'Un rapport complet, imprimable, envoyé aussi par e-mail']);
    /* Grand format (lot D2) : annoncé seulement quand l'illustration est active sur la page. */
    if (config && config.imageEnabled) lignes.push([null, 'Votre rêve en grand format, sans filigrane, dans le style choisi']);

    clear(host);
    /* Revue du 04/09 : trois bénéfices lisibles d'abord, le détail chiffré dans un bloc dépliable. */
    var benefices = el('ul', 'dl-unlock');
    benefices.appendChild(el('li', null, 'Les liens entre les scènes de votre récit, question par question'));
    benefices.appendChild(el('li', null, 'Les interprétations possibles, selon l’angle que vous avez choisi'));
    benefices.appendChild(el('li', null, 'Des pistes de réflexion, et ce que votre rêve ne veut probablement pas dire'));
    if (config && config.imageEnabled) benefices.appendChild(el('li', null, 'Votre rêve en grand format, sans filigrane, recréé avec le modèle avancé (il peut différer de l’aperçu offert)'));
    host.appendChild(benefices);
    var detail = document.createElement('details');
    detail.className = 'dl-inventaire__detail';
    var resume = el('summary', null, 'Le détail, sur votre rêve');
    detail.appendChild(resume);
    var ul = el('ul', 'dl-inventaire');
    lignes.forEach(function (l) {
      var li = el('li');
      if (l[0]) li.appendChild(el('b', 'dl-inventaire__nb', l[0]));
      li.appendChild(document.createTextNode(l[1]));
      ul.appendChild(li);
    });
    detail.appendChild(ul);
    host.appendChild(detail);

    /* Preuve que le système a lu CE rêve : ses propres symboles, nommés. */
    if (symboles.length) {
      var noms = symboles.slice(0, 4).map(function (x) { return x.name; });
      detail.appendChild(el('p', 'dl-inventaire__preuve',
        'Construite à partir de ce que vous avez raconté : ' + noms.join(', ') + '.'));
    }
  }

  function renderLocked(q, free) {
    var host = q('[data-dream-locked]');
    var intro = q('[data-dream-paywall-intro]');
    var titre = q('[data-dream-paywall-titre]');
    if (!host) return;
    clear(host);

    if (intro && free.locked_insight) intro.textContent = free.locked_insight;

    var items = (free.locked_questions || []).filter(function (x) { return x && x.title; });
    if (!items.length) {
      if (intro && !free.locked_insight && free.paywall_teaser) intro.textContent = free.paywall_teaser;
      return;
    }

    /* Maquette « Résultat hybride » (04/09/2026) : le titre de l'offre est la première question
       restée ouverte, formulée comme une question ; la liste n'a ni amorce ni aperçu. */
    if (titre) {
      var premiere = String(items[0].title).trim();
      titre.textContent = /[?!]$/.test(premiere) ? premiere : premiere + ' ?';
    }
    /* Aperçu court de la réponse 1 (revue du 04/09) : une seule preuve de profondeur, jamais
       inventée ici — le début réel de la réponse, tel que l'analyse complète le reprendra. */
    var apercu = (free.locked_preview || '').trim();
    if (apercu) {
      var carte = el('div', 'dl-apercu dl-apercu--court');
      carte.appendChild(el('p', 'dl-apercu__label', 'Début de la réponse'));
      var clip = el('div', 'dl-apercu__clip');
      clip.appendChild(el('p', 'dl-apercu__texte', apercu));
      clip.appendChild(el('span', 'dl-apercu__fondu'));
      carte.appendChild(clip);
      carte.appendChild(el('p', 'dl-apercu__verrou', 'La suite dans votre analyse complète'));
      host.appendChild(carte);
    }
    /* La première question est déjà le titre : la liste reprend les suivantes, sans la répéter. */
    var suivantes = items.slice(1);
    if (suivantes.length) {
      host.appendChild(el('p', 'dl-paywall__label', 'Et aussi'));
      var list = el('ol', 'dl-qlist');
      suivantes.forEach(function (item, i) {
        var li = el('li');
        li.appendChild(el('span', 'dl-qlist__num', String(i + 2)));
        li.appendChild(el('span', 'dl-qlist__texte', item.title));
        list.appendChild(li);
      });
      host.appendChild(list);
    }
    var n = items.length;
    host.appendChild(el('p', 'dl-locked-count', n === 1
      ? 'Débloquez cette interprétation.'
      : 'Débloquez les réponses aux ' + n + ' questions ci-dessus.'));
  }

  /* ---------- rapport (analyse complète) ----------
     Maquette « Rapport analyse » du 03/09/2026 : couverture, sommaire, chapitres numérotés
     pour les questions annoncées avant l'achat, sens global, symboles, lectures, encart
     « ce que le rêve ne dit pas », pistes, pied. Tout le contenu IA passe par textContent. */

  function paragraphes(texte, cls) {
    return String(texte).split(/\n\s*\n/).map(function (t) { return t.trim(); }).filter(Boolean)
      .map(function (t) { return el('p', cls || 'dl-rx__p', t); });
  }
  function premierePhrase(t) {
    var m = String(t).match(/^[^.!?]+[.!?]/);
    return (m ? m[0] : String(t)).trim();
  }
  function numero(i) { return (i < 9 ? '0' : '') + (i + 1); }

  function renderFull(target, paid, config, opts) {
    opts = opts || (config && config.rendu) || {};
    clear(target);
    var niveau = opts.niveau || 'h1';
    var sym = (paid.symbol_analysis || []).filter(function (x) { return x && x.symbol; });
    var reponses = (paid.locked_answers || []).filter(function (x) { return x && x.question && x.answer; });
    var pistes = (paid.personal_advice || []).filter(Boolean);
    var uid = 'dlrx-' + Math.random().toString(36).slice(2, 7);

    /* Couverture */
    var cover = el('header', 'dl-rx__cover');
    cover.setAttribute('data-print', 'cover');
    var grille = el('div', 'dl-rx__cover-grid');
    var ct = el('div', 'dl-rx__cover-texte');
    ct.appendChild(el('p', 'dl-rx__badge', 'Analyse complète'));
    ct.appendChild(el(niveau, 'dl-rx__titre', 'Votre analyse personnalisée'));
    if (paid.expires_at) {
      var meta = el('p', 'dl-rx__meta');
      meta.appendChild(el('span', null, 'Lien privé'));
      meta.appendChild(el('span', null, 'Accessible jusqu’au ' + paid.expires_at));
      ct.appendChild(meta);
    }
    if (paid.opening_summary) ct.appendChild(el('p', 'dl-rx__lede', paid.opening_summary));
    if (sym.length) {
      ct.appendChild(el('p', 'dl-rx__label', 'Éléments retenus de votre récit'));
      var chips = el('div', 'dl-rx__chips');
      sym.slice(0, 6).forEach(function (x) { chips.appendChild(el('span', 'dl-rx__chip', x.symbol)); });
      ct.appendChild(chips);
    }
    var imprimer = el('button', 'dl-btn dl-btn--ghost dl-btn--auto dl-print-hide', 'Imprimer / enregistrer en PDF');
    imprimer.type = 'button';
    imprimer.addEventListener('click', function () { window.print(); });
    ct.appendChild(imprimer);
    grille.appendChild(ct);
    /* L'illustration du rêve (lot D), si elle existe, prend la place du lama sur la couverture. */
    if (opts.image) {
      var illuWrap = el('div', 'dl-rx__cover-lama dl-rx__cover-illu');
      var illu = document.createElement('img');
      illu.src = opts.image; illu.alt = 'Illustration de votre rêve';
      illu.width = 232; illu.height = 232; illu.decoding = 'async';
      illuWrap.appendChild(illu); grille.appendChild(illuWrap);
    } else if (config && config.lamaLit) {
      var lamaWrap = el('div', 'dl-rx__cover-lama');
      var lama = document.createElement('img');
      lama.src = config.lamaLit; lama.alt = 'Le lama DailyLama lisant une analyse';
      lama.width = 232; lama.height = 232; lama.decoding = 'async';
      lamaWrap.appendChild(lama); grille.appendChild(lamaWrap);
    }
    cover.appendChild(grille);

    /* Corps : sommaire collant + contenu */
    var corps = el('div', 'dl-rx__body');
    var toc = el('nav', 'dl-rx__toc dl-print-hide');
    toc.setAttribute('aria-label', 'Sommaire');
    var tocBox = el('div', 'dl-rx__toc-box');
    tocBox.appendChild(el('p', 'dl-rx__label', 'Sommaire'));
    var tocList = el('div', 'dl-rx__toc-list');
    tocBox.appendChild(tocList); toc.appendChild(tocBox);
    var contenu = el('div', 'dl-rx__content');
    function ancre(id, libelle) {
      var lien = el('a', 'dl-rx__toc-link', libelle); lien.href = '#' + uid + '-' + id;
      tocList.appendChild(lien); return uid + '-' + id;
    }

    var lignes = [];
    if (opts.free && opts.free.dominant_theme) lignes.push(['Thème dominant', opts.free.dominant_theme]);
    if (paid.global_meaning) lignes.push(['Sens global', premierePhrase(paid.global_meaning)]);
    if (sym.length) lignes.push(['Symboles principaux', sym.map(function (x) { return x.symbol; }).join(' · ')]);
    if (lignes.length >= 2) {
      var bref = el('section', 'dl-rx__brief'); bref.id = ancre('bref', 'En bref'); bref.setAttribute('data-print', 'chapter');
      bref.appendChild(el('p', 'dl-rx__label', 'Votre rêve en bref'));
      lignes.forEach(function (l) {
        var r = el('div', 'dl-rx__brief-row');
        r.appendChild(el('div', 'dl-rx__brief-key', l[0])); r.appendChild(el('div', 'dl-rx__brief-val', l[1]));
        bref.appendChild(r);
      });
      bref.appendChild(el('p', 'dl-rx__brief-note', 'Une lecture possible, pas une vérité arrêtée.'));
      contenu.appendChild(bref);
    }

    if (reponses.length) {
      var titreQ = reponses.length > 1 ? 'Vos ' + reponses.length + ' questions' : 'Votre question';
      var tete = el('div', 'dl-rx__head'); tete.id = ancre('questions', titreQ);
      tete.appendChild(el('p', 'dl-rx__label', titreQ));
      tete.appendChild(el('p', 'dl-rx__intro', reponses.length > 1
        ? 'Les questions affichées avant votre commande, reprises dans le même ordre et développées.'
        : 'La question affichée avant votre commande, développée.'));
      contenu.appendChild(tete);
      reponses.forEach(function (a, i) {
        var ch = el('article', 'dl-rx__chapitre'); ch.setAttribute('data-print', 'chapter');
        var nr = el('div', 'dl-rx__num-row');
        nr.appendChild(el('span', 'dl-rx__num', numero(i))); nr.appendChild(el('span', 'dl-rx__rule'));
        ch.appendChild(nr);
        ch.appendChild(el('h2', 'dl-rx__h2', a.question));
        paragraphes(a.answer).forEach(function (x) { ch.appendChild(x); });
        contenu.appendChild(ch);
      });
    }

    if (paid.global_meaning) {
      var g = el('section', 'dl-rx__chapitre'); g.id = ancre('sens', 'Sens global'); g.setAttribute('data-print', 'chapter');
      g.appendChild(el('h2', 'dl-rx__h2', 'Sens global du rêve'));
      paragraphes(paid.global_meaning).forEach(function (x) { g.appendChild(x); });
      contenu.appendChild(g);
    }

    if (sym.length) {
      var ts = el('div', 'dl-rx__head'); ts.id = ancre('symboles', 'Symboles');
      ts.appendChild(el('h2', 'dl-rx__h2', 'Analyse symbole par symbole'));
      ts.appendChild(el('p', 'dl-rx__intro', 'Chaque symbole est d’abord replacé dans sa lecture générale, puis dans le rôle précis qu’il joue dans votre récit.'));
      contenu.appendChild(ts);
      sym.forEach(function (x) {
        var bloc = el('article', 'dl-rx__symbole'); bloc.setAttribute('data-print', 'chapter');
        bloc.appendChild(el('h3', 'dl-rx__h3', x.symbol));
        if (x.meaning) bloc.appendChild(el('p', 'dl-rx__p', x.meaning));
        if (x.in_this_dream) {
          var role = el('div', 'dl-rx__role');
          role.appendChild(el('div', 'dl-rx__role-key', 'Dans votre rêve'));
          role.appendChild(el('div', 'dl-rx__role-val', x.in_this_dream));
          bloc.appendChild(role);
        }
        contenu.appendChild(bloc);
      });
    }

    var lectures = [
      ['Lecture émotionnelle', paid.emotional_reading],
      ['Lecture spirituelle', paid.spiritual_reading],
      ['Lecture islamique', paid.islamic_reading_optional]
    ].filter(function (l) { return l[1]; });
    if (lectures.length) {
      var tl = el('div', 'dl-rx__head'); tl.id = ancre('lectures', 'Lectures');
      tl.appendChild(el('h2', 'dl-rx__h2', 'Lectures du rêve'));
      contenu.appendChild(tl);
      lectures.forEach(function (l) {
        var bloc = el('article', 'dl-rx__chapitre dl-rx__chapitre--serre'); bloc.setAttribute('data-print', 'chapter');
        bloc.appendChild(el('h3', 'dl-rx__h3', l[0]));
        paragraphes(l[1]).forEach(function (x) { bloc.appendChild(x); });
        contenu.appendChild(bloc);
      });
    }

    if (paid.what_it_probably_does_not_mean) {
      var w = el('section', 'dl-rx__warn'); w.id = ancre('ne-dit-pas', 'Ce que le rêve ne dit pas'); w.setAttribute('data-print', 'chapter');
      w.appendChild(el('h2', 'dl-rx__warn-titre', 'Ce que ce rêve ne veut probablement pas dire'));
      paragraphes(paid.what_it_probably_does_not_mean, 'dl-rx__warn-texte').forEach(function (x) { w.appendChild(x); });
      contenu.appendChild(w);
    }

    if (pistes.length) {
      var tp = el('div', 'dl-rx__head'); tp.id = ancre('pistes', 'Pistes de réflexion');
      tp.appendChild(el('h2', 'dl-rx__h2', 'Pistes de réflexion'));
      tp.appendChild(el('p', 'dl-rx__intro', (pistes.length > 1 ? pistes.length + ' invitations' : 'Une invitation')
        + ' à relire votre rêve. Rien n’est enregistré ici : notez vos réponses où vous le souhaitez.'));
      contenu.appendChild(tp);
      var liste = el('ol', 'dl-rx__pistes');
      pistes.forEach(function (t, i) {
        var li = el('li', 'dl-rx__piste');
        li.appendChild(el('span', 'dl-rx__piste-num', numero(i)));
        li.appendChild(el('p', 'dl-rx__piste-texte', t));
        liste.appendChild(li);
      });
      contenu.appendChild(liste);
    }

    var pied = el('footer', 'dl-rx__foot'); pied.setAttribute('data-print', 'chapter');
    if (paid.closing_sentence) pied.appendChild(el('p', 'dl-rx__conclusion', paid.closing_sentence));
    pied.appendChild(el('p', 'dl-rx__disclaimer', paid.disclaimer
      || 'Cette analyse est une lecture symbolique et personnelle destinée à l’introspection. Elle ne constitue pas un avis médical, psychologique, juridique ou religieux, et ne prédit pas l’avenir.'));
    if (paid.expires_at) {
      var ex = el('div', 'dl-rx__expiry');
      ex.appendChild(el('span', 'dl-rx__expiry-label', 'Lien privé'));
      ex.appendChild(el('span', 'dl-rx__expiry-val', 'Accessible jusqu’au ' + paid.expires_at + ' · Imprimez ou enregistrez votre analyse pour la conserver.'));
      pied.appendChild(ex);
    }
    /* Les actions vivent dans le gabarit (le lien « Analyser un autre rêve » y garde son
       marqueur de suivi) : on les déplace dans le pied, sans les recréer. */
    if (opts.actions) { opts.actions.classList.remove('dl-hidden'); pied.appendChild(opts.actions); }
    contenu.appendChild(pied);

    corps.appendChild(toc); corps.appendChild(contenu);
    target.appendChild(cover); target.appendChild(corps);
    if (tocList.childNodes.length < 3) toc.classList.add('dl-hidden');

    renderReco(target, paid, config);
  }

  function euros(centimes) { return (centimes / 100).toFixed(2).replace('.', ',') + ' €'; }

  /* Recommandation boutique, en carte brune après le rapport. Image, titre, URL et handle
     viennent du backend ; prix et disponibilité sont lus sur la fiche produit publique de la
     boutique, même objet que le titre. Une donnée manquante annule l'offre entière : jamais
     de carte partielle (§13). Aucune remise n'est affichée tant qu'aucune source ne la fournit. */
  function renderReco(target, paid, config) {
    var reco = paid.recommended_product;
    if (!reco || !reco.url || !reco.title || !reco.image || !reco.handle) return;

    var outer = el('aside', 'dl-rx__cross dl-print-hide dl-hidden');
    outer.setAttribute('aria-label', 'Recommandation boutique');
    var carte = el('div', 'dl-reco');
    var media = el('div', 'dl-reco__media');
    var img = document.createElement('img');
    img.src = reco.image; img.alt = reco.title; img.loading = 'lazy'; img.width = 228; img.height = 228;
    media.appendChild(img); carte.appendChild(media);

    var texte = el('div', 'dl-reco__texte');
    texte.appendChild(el('p', 'dl-reco__eyebrow', 'Pour prolonger votre intention'));
    texte.appendChild(el('h2', 'dl-reco__titre', 'Un bracelet choisi autour de votre lecture'));
    /* Le chemin gratuit ne transmet pas d'intention : on n'annonce que ce qu'on sait. */
    if (paid.recommended_intention) {
      var pilule = el('p', 'dl-reco__intention');
      pilule.appendChild(el('span', 'dl-reco__intention-key', 'Fil conducteur'));
      pilule.appendChild(el('span', 'dl-reco__intention-val', paid.recommended_intention));
      texte.appendChild(pilule);
    }
    if (reco.reason) texte.appendChild(el('p', 'dl-reco__raison', reco.reason));
    var produit = el('div', 'dl-reco__produit');
    produit.appendChild(el('p', 'dl-reco__select', 'Sélection DailyLama'));
    produit.appendChild(el('p', 'dl-reco__nom', reco.title));
    var prix = el('p', 'dl-reco__prix');
    produit.appendChild(prix); texte.appendChild(produit);

    var lien = el('a', 'dl-reco__cta', 'Voir ce bracelet');
    lien.href = reco.url; lien.target = '_blank'; lien.rel = 'noopener';
    lien.addEventListener('click', function () {
      track(config, 'dream_product_reco_click', {
        analysis_id: paid.analysis_id, recommended_product_handle: reco.handle,
        recommended_intention: paid.recommended_intention
      });
    });
    texte.appendChild(lien);
    texte.appendChild(el('p', 'dl-reco__micro',
      'Une proposition symbolique, sans promesse d’effet médical, psychologique ou sur le sommeil.'));
    carte.appendChild(texte); outer.appendChild(carte); target.appendChild(outer);

    fetch('/products/' + encodeURIComponent(reco.handle) + '.js')
      .then(function (r) { return r.ok ? r.json() : Promise.reject(new Error('produit')); })
      .then(function (p) {
        if (!p || p.available === false || !(p.price > 0)) throw new Error('indisponible');
        var dispo = (p.variants || []).filter(function (v) { return v.available; }).map(function (v) { return v.price; });
        var min = dispo.length ? Math.min.apply(null, dispo) : p.price;
        var max = dispo.length ? Math.max.apply(null, dispo) : p.price;
        var libelle = (max > min ? 'dès ' : '') + euros(min);
        prix.textContent = libelle;
        lien.textContent = 'Voir ce bracelet — ' + libelle;
        show(outer);
        track(config, 'dream_product_reco_view', {
          analysis_id: paid.analysis_id, recommended_product_handle: reco.handle,
          recommended_intention: paid.recommended_intention
        });
      })
      .catch(function () { if (outer.parentNode) outer.parentNode.removeChild(outer); });
  }

  /* ---------- outil principal ---------- */

  function initTool(root) {
    var config = cfg(root);

    /* Provenance transmise par le CTA des articles (?dl_a=&dl_p=). Sans elle, toutes
       les analyses étaient enregistrées comme « direct » et l'on ne pouvait pas savoir
       quels articles amènent réellement du monde. */
    try {
      var q = new URLSearchParams(location.search);
      var venu = q.get('dl_a');
      if (venu) {
        config.articleHandle = venu;
        if (!config.dreamTopic) config.dreamTopic = venu;
      }
      var pos = q.get('dl_p');
      if (pos) config.ctaPosition = pos;

      /* Bras du test de copie des CTA d'articles (?dl_v=). Il suit le visiteur
         jusqu'au formulaire pour lire le tunnel par bras, pas seulement le clic :
         un CTA intrigant peut attirer des clics faibles qui ne vont nulle part. */
      var bv = q.get('dl_v');
      if (bv === 'generique_v1' || bv === 'question_v1') config.ctaVariant = bv;

      /* Symbole transmis par le CTA (?dl_s=). Quelqu'un qui vient de lire « rêver de
         serpent » arrivait devant un formulaire vierge qui lui redemandait tout depuis
         le début. On lui rappelle ce qu'il cherchait et on ne lui demande que la suite. */
      var symbole = q.get('dl_s');
      if (symbole) {
        symbole = symbole.replace(/[<>]/g, '').trim().slice(0, 40);
        config.dreamTopic = symbole;
        var titre = root.querySelector('[data-dream-form] h2');
        var sous = root.querySelector('[data-dream-form] .dl-sub');
        var aide = root.querySelector('[data-dream-form] .dl-aide');
        var zone = root.querySelector('[data-dream-text]');
        var libelle = root.querySelector('label[for="dl-dream"]');
        /* Le symbole est cité entre guillemets : « araignée », « couper les cheveux »
           et « serpent » n'appellent pas le même déterminant, et deviner le genre
           d'un mot arbitraire produirait du mauvais français à grande échelle. */
        var cite = '« ' + symbole + ' »';
        if (titre) { titre.textContent = cite + ' : que s’est-il passé dans votre rêve ?'; titre.classList.remove('dl-sr'); }
        if (sous) { sous.textContent = 'Vous venez de lire l’interprétation générale. Dites-nous ce que vous, vous avez vu.'; sous.classList.remove('dl-sr'); }
        if (libelle) libelle.textContent = 'Votre rêve, en quelques mots';
        if (aide) aide.textContent = 'Même quelques détails suffisent : ce que vous avez vu, où vous étiez, '
          + 'ce que vous avez ressenti. Si vous ne gardez qu’une image, écrivez-la.';
        if (zone) zone.setAttribute('placeholder',
          'Exemple : je me souviens surtout que…');
      }
    } catch (e) {}

    var q = function (sel) { return root.querySelector(sel); };

    var form = q('[data-dream-form]');
    if (!form) return;

    var textarea = q('[data-dream-text]');
    var counter = q('[data-dream-counter]');
    var consent = q('[data-dream-consent]');
    var sensible = q('[data-dream-consent-sensible]');
    // La mention des convictions ne concerne que les angles spirituel et islamique :
    // on l'affiche alors plutôt que de l'imposer à tout le monde en permanence.
    function majSensible() {
      if (!sensible) return;
      var a = q('[name="dl-angle"]:checked');
      var v = a ? a.value : '';
      if (v === 'islamique' || v === 'spirituelle') sensible.classList.remove('dl-hidden');
      else sensible.classList.add('dl-hidden');
    }
    Array.prototype.forEach.call(root.querySelectorAll('[name="dl-angle"]'), function (r) {
      r.addEventListener('change', majSensible);
    });
    majSensible();

    /* Test « continuité article islamique → angle islamique » (03/09/2026).
       Constat : 82,8 % des visiteurs venus d'un article islamique choisissent
       l'angle islamique — mais ils arrivent devant « Symbolique » coché et
       doivent corriger. Le CTA transmet ?dl_angle= quand l'article est
       explicitement islamique ; ici on le suggère, on ne l'impose pas.

       Bras tiré une fois par navigateur et conservé : la même personne voit
       toujours le même comportement, sinon le test compare deux expériences
       incohérentes. Contrôle = comportement actuel, la suggestion est ignorée.
       Variante = angle présélectionné ET une phrase qui dit d'où il vient, avec
       les quatre choix toujours accessibles. L'angle suggéré, l'angle retenu et
       le bras partent avec dream_form_submit. */
    config.suggestedAngle = null;
    config.abAngle = null;
    try {
      var suggere = new URLSearchParams(location.search).get('dl_angle');
      if (suggere && root.querySelector('[name="dl-angle"][value="' + suggere + '"]')) {
        config.suggestedAngle = suggere;
        var bras = localStorage.getItem('dl_ab_angle');
        if (bras !== 'controle' && bras !== 'variante') {
          bras = Math.random() < 0.5 ? 'controle' : 'variante';
          localStorage.setItem('dl_ab_angle', bras);
        }
        config.abAngle = bras;
        if (bras === 'variante') {
          var radio = root.querySelector('[name="dl-angle"][value="' + suggere + '"]');
          radio.checked = true;
          majSensible();
          var noteAngle = q('[data-dream-angle-note]');
          if (noteAngle) noteAngle.classList.remove('dl-hidden');
        }
      }
    } catch (e) { /* stockage indisponible : pas de test, comportement actuel */ }
    /* Pastilles de contexte : un seul choix, recliquer retire. La valeur va dans le champ
       caché [data-dream-context], que la charge utile lit comme avant (life_context). */
    var champContexte = q('[data-dream-context]');
    Array.prototype.forEach.call(root.querySelectorAll('[data-dream-contexte]'), function (b) {
      b.addEventListener('click', function () {
        var actif = b.getAttribute('aria-pressed') === 'true';
        Array.prototype.forEach.call(root.querySelectorAll('[data-dream-contexte]'), function (x) { x.setAttribute('aria-pressed', 'false'); });
        if (!actif) b.setAttribute('aria-pressed', 'true');
        if (champContexte) champContexte.value = actif ? '' : (b.getAttribute('data-dream-contexte') || '');
      });
    });
    var consentBox = q('[data-dream-consent-box]');
    var marketing = q('[data-dream-marketing]');
    var submit = q('[data-dream-submit]');
    var submitLabel = q('[data-dream-submit-label]');
    var formMsg = q('[data-dream-form-msg]');
    var resultsSection = q('[data-dream-results]');
    var freeTarget = q('[data-dream-free]');
    var freeSkeleton = q('[data-dream-skeleton]');
    var paywall = q('[data-dream-paywall]');
    var unlock = q('[data-dream-unlock]');
    var bump = q('[data-dream-bump]');
    var bumpWrap = q('[data-dream-bump-wrap]');
    var fullSection = q('[data-dream-full-section]');
    var fullTarget = q('[data-dream-full]');

    var state = { analysisId: null, accessKey: null, email: null, busy: false, started: false };
    /* Libellé du bouton tel que servi par le gabarit : c'est lui qu'on remet après l'appel,
       et non un texte écrit ici qui divergerait du gabarit. */
    var libelleSoumettre = submitLabel && submitLabel.textContent.trim() ? submitLabel.textContent.trim() : 'Analyser mon rêve gratuitement →';

    if (!config.enabled) { hide(form); return; }
    if (!config.enableEbookBump) hide(bumpWrap);

    /* compteur + démarrage formulaire */
    /* Espace fine insécable pour les milliers, usage français. */
    function milliers(n) { return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '\u202f'); }

    function updateCounter() {
      var len = textarea.value.trim().length;

      /* Le champ vire au rouge à la soumission ; sans cette remise à zéro il y
         restait pendant qu'on le corrigeait. */
      if (textarea.getAttribute('aria-invalid') === 'true' &&
          len >= config.minChars && len <= config.maxChars) {
        textarea.removeAttribute('aria-invalid');
        message('', '');
      }

      if (!counter) return;

      /* « 46 / 4000 » se lisait comme un minimum de 4000 à atteindre, alors que
         le seul seuil bloquant est le minimum. On n'affiche donc le maximum que
         lorsqu'il devient la contrainte réelle. */
      var proche = Math.round(config.maxChars * 0.9);
      var alerte = false;
      if (len === 0) {
        counter.textContent = milliers(config.maxChars) + ' caractères maximum';
      } else if (len < config.minChars) {
        var reste = config.minChars - len;
        counter.textContent = 'Encore ' + reste + (reste > 1 ? ' caractères' : ' caractère') +
                              ' pour lancer l’analyse';
        alerte = true;
      } else if (len > config.maxChars) {
        counter.textContent = milliers(len) + ' / ' + milliers(config.maxChars) +
                              ' caractères — récit trop long';
        alerte = true;
      } else if (len > proche) {
        counter.textContent = milliers(len) + ' / ' + milliers(config.maxChars) + ' caractères';
        alerte = true;
      } else {
        counter.textContent = milliers(len) + (len > 1 ? ' caractères' : ' caractère');
      }
      counter.classList.toggle('is-warn', alerte);
      if (!state.started && len > 0) {
        state.started = true;
        track(config, 'dream_form_start', {
          source_url: location.href,
          article_handle: config.articleHandle || null,
          cta_position: config.ctaPosition || null
        });
      }
    }
    textarea.addEventListener('input', updateCounter);
    updateCounter();

    function setBusy(on, label) {
      state.busy = on;
      submit.disabled = on;
      clear(submitLabel);
      if (on) {
        submitLabel.appendChild(el('span', 'dl-spinner'));
        submitLabel.appendChild(document.createTextNode(' ' + (label || 'Analyse en cours…')));
      } else {
        submitLabel.textContent = label || libelleSoumettre;
      }
    }

    function message(kind, text) {
      clear(formMsg);
      if (!text) { hide(formMsg); return; }
      formMsg.className = 'dl-msg dl-msg--' + kind;
      formMsg.textContent = text;
      show(formMsg);
    }

    /* soumission */
    form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      if (state.busy) return;

      var text = textarea.value.trim();
      if (text.length < config.minChars) {
        textarea.setAttribute('aria-invalid', 'true');
        message('error', 'Ajoutez quelques détails pour que l’analyse soit vraiment personnalisée (' + config.minChars + ' caractères minimum).');
        textarea.focus();
        return;
      }
      if (text.length > config.maxChars) {
        message('error', 'Votre récit dépasse ' + config.maxChars + ' caractères. Gardez l’essentiel du rêve.');
        return;
      }
      if (consent && !consent.checked) {
        consentBox.classList.add('is-invalid');
        message('error', 'Merci de confirmer que vous avez compris la nature de cette lecture avant de continuer.');
        return;
      }
      textarea.removeAttribute('aria-invalid');
      consentBox.classList.remove('is-invalid');
      message(null);

      var payload = {
        dream_text: text,
        main_emotion: q('[data-dream-emotion]').value,
        life_context: q('[data-dream-context]').value,
        interpretation_angle: (root.querySelector('[name="dl-angle"]:checked') || {}).value || 'symbolique',
        first_name: (q('[data-dream-name]').value || '').trim(),
        consent: true,
        marketing_consent: !!(marketing && marketing.checked),
        source_url: location.href,
        article_handle: config.articleHandle || null,
        dream_topic: config.dreamTopic || null
      };

      track(config, 'dream_form_submit', {
        interpretation_angle: payload.interpretation_angle,
        main_emotion: payload.main_emotion,
        /* Les trois clés du test de continuité. `selected_angle` doublonne
           `interpretation_angle` à dessein : celui-ci n'a jamais été retenu par
           l'allowlist serveur, celui-là l'est. */
        suggested_angle: config.suggestedAngle || null,
        selected_angle: payload.interpretation_angle,
        ab_angle: config.abAngle || null,
        /* Bras du test de copie des CTA, transmis par ?dl_v= : permet de lire
           « formulaires envoyés par bras », et plus loin les paiements. */
        cta_variant: config.ctaVariant || null,
        source_url: location.href
      });

      setBusy(true);
      show(resultsSection);
      show(freeSkeleton);
      hide(paywall);
      clear(freeTarget);

      fetch(config.apiBase + '/dream/free-analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, status: r.status, body: b }; }); })
        .then(function (res) {
          setBusy(false);
          hide(freeSkeleton);

          if (res.status === 429) {
            var msg = res.body && res.body.message
              ? res.body.message
              : 'Vous avez atteint le nombre d’analyses gratuites pour le moment. Revenez un peu plus tard, votre place est gardée.';
            message('info', msg);
            hide(resultsSection);
            track(config, 'dream_error', { reason: 'rate_limited' });
            return;
          }
          if (res.status === 409 && res.body && res.body.analysis_id) {
            // Le serveur ne remet jamais une clé d'accès en circulation : on ne peut
            // reprendre le tunnel que si cette analyse est bien la nôtre.
            var mine = restore();
            renderFree(freeTarget, res.body.free_result);
            renderLocked(q, res.body.free_result);
            renderInventaire(q, res.body.free_result, payload.interpretation_angle, config);
            if (mine && mine.analysis_id === res.body.analysis_id && mine.access_key) {
              state.analysisId = mine.analysis_id;
              state.accessKey = mine.access_key;
              copiePreparer();
              preparerImage(null, false, res.body.free_result);
              message('info', 'Vous avez déjà reçu une lecture pour ce rêve — la voici.');
              show(paywall);
            } else {
              message('info', 'Cette lecture a déjà été générée. Modifiez ou précisez votre récit pour obtenir une nouvelle analyse.');
              hide(paywall);
            }
            return;
          }
          if (!res.ok || !res.body || !res.body.free_result) {
            message('error', (res.body && res.body.message) || 'L’analyse n’a pas pu être générée pour le moment. Votre récit est conservé : réessayez dans quelques instants.');
            hide(resultsSection);
            /* Le récit reste dans le champ et le bouton est déjà rendu (setBusy(false) plus
               haut) : réessayer ne coûte qu'un clic. On y ramène l'attention. */
            try { submit.focus(); } catch (e) {}
            track(config, 'dream_error', { reason: 'api_error', status: res.status });
            return;
          }

          state.analysisId = res.body.analysis_id;
          state.accessKey = res.body.access_key;
          store({ analysis_id: state.analysisId, access_key: state.accessKey, status: 'free', ts: Date.now() });

          renderFree(freeTarget, res.body.free_result);
          /* La carte pierre vient APRÈS l'offre payante. Placée dans la lecture
             gratuite, elle disputait l'attention au paywall et pouvait donner à croire
             que toute l'analyse sert à vendre un bracelet. */
          var apres = q('[data-dream-reco-apres]');
          if (apres && res.body.recommended_product) {
            clear(apres);
            renderReco(apres, {
              recommended_product: res.body.recommended_product,
              recommended_stones: res.body.recommended_stones,
              analysis_id: res.body.analysis_id
            }, config);
          }
          renderLocked(q, res.body.free_result);
          renderInventaire(q, res.body.free_result, payload.interpretation_angle, config);
          state.email = null;
          copiePreparer();
          preparerImage(null, false, res.body.free_result);
          /* remonterOffre(paywall) : plus appelé depuis le 08/09/2026, voir le commentaire de la fonction. */
          show(paywall);
          track(config, 'dream_free_result_view', { analysis_id: state.analysisId });
          observerPaywall(config, paywall, state.analysisId);
          if (config.enableEbookBump) track(config, 'dream_ebook_bump_view', { analysis_id: state.analysisId });
          allerAuResultat(resultsSection);
        })
        .catch(function () {
          setBusy(false);
          hide(freeSkeleton);
          message('error', 'Connexion interrompue. Vérifiez votre réseau et réessayez.');
          track(config, 'dream_error', { reason: 'network' });
        });
    });

    /* Capture d'adresse (lot B, maquette hybride 04/09/2026) : le seul champ e-mail de la page.
       L'adresse est enregistrée sur l'analyse au stade gratuit, avec la case des suites si elle
       est cochée ; la lecture (et l'illustration) partent par e-mail, l'image se déverrouille.
       Ce n'est jamais une intention de paiement : le serveur distingue par `purpose`. */
    var copie = q('[data-dream-copie]');
    var copieEnvoyer = q('[data-dream-copie-envoyer]');
    function copieErreur(texte) {
      var champ = q('[data-dream-copie-email]'), err = q('[data-dream-copie-erreur]');
      if (err) err.textContent = texte || '';
      if (champ) { if (texte) { champ.setAttribute('aria-invalid', 'true'); champ.focus(); } else champ.removeAttribute('aria-invalid'); }
    }
    function copieOccupe(oui) {
      if (!copieEnvoyer) return;
      copieEnvoyer.disabled = !!oui;
      copieEnvoyer.setAttribute('aria-busy', oui ? 'true' : 'false');
      var lib = q('[data-dream-copie-libelle]');
      if (!lib) return;
      if (oui) { lib.dataset.dlLibelle = lib.textContent; clear(lib); lib.appendChild(el('span', 'dl-spinner')); lib.appendChild(document.createTextNode(' Envoi en cours…')); }
      else if (lib.dataset.dlLibelle) { lib.textContent = lib.dataset.dlLibelle; delete lib.dataset.dlLibelle; }
    }
    /* Bande de confirmation : l'adresse réelle, ou une mention neutre à la reprise par lien. */
    function copieConfirmee(email) {
      if (!copie) return;
      copie.classList.add('is-envoyee');
      var ok = q('[data-dream-copie-ok]'), msg = q('[data-dream-copie-msg]');
      var avecImage = imageBloc && !imageBloc.classList.contains('dl-hidden') && !imageBloc.classList.contains('is-indisponible');
      if (msg) msg.textContent = email
        ? (avecImage ? 'Illustration et lecture envoyées à ' : 'Lecture envoyée à ') + email
        : 'Lecture déjà envoyée à votre adresse.';
      if (ok) show(ok);
      var note = q('[data-dream-paywall-mail]');
      if (note) { note.textContent = email ? 'Une copie sera envoyée à ' + email + '.' : 'Une copie sera envoyée à l’adresse déjà enregistrée.'; show(note); }
      /* L'adresse est connue : le champ de livraison se replie, il ne sera pas redemandé. */
      var livraisonBloc = q('[data-dream-livraison]');
      if (livraisonBloc) hide(livraisonBloc);
      if (livraisonBloc && email) { var champLiv = q('[data-dream-email]'); if (champLiv) champLiv.value = email; }
    }
    function copiePreparer() {
      if (!copie) return;
      copie.classList.remove('is-envoyee');
      var ok = q('[data-dream-copie-ok]'); if (ok) hide(ok);
      copieErreur('');
      copieOccupe(false);
      var note = q('[data-dream-paywall-mail]');
      if (note) { note.textContent = state.email ? 'Une copie sera envoyée à ' + state.email + '.' : ''; if (state.email) show(note); else hide(note); }
      var livraisonBloc = q('[data-dream-livraison]');
      if (livraisonBloc) { if (state.email) hide(livraisonBloc); else show(livraisonBloc); }
      var champLiv = q('[data-dream-email]');
      if (champLiv) { champLiv.value = state.email || ''; champLiv.removeAttribute('aria-invalid'); }
      var errLiv = q('[data-dream-email-erreur]');
      if (errLiv) errLiv.textContent = '';
    }
    if (copie && copieEnvoyer) copieEnvoyer.addEventListener('click', function () {
      if (!state.analysisId || copieEnvoyer.disabled) return;
      var champ = q('[data-dream-copie-email]');
      var murCase = q('[data-dream-mur]'), murPseudo = q('[data-dream-mur-pseudo]');
      var email = champ ? champ.value.trim() : '';
      if (!email || !emailPlausible(email)) {
        copieErreur('Vérifiez votre adresse : il manque un @ ou le nom de domaine.');
        return;
      }
      copieErreur('');
      copieOccupe(true);
      fetch(config.apiBase + '/dream/analysis/' + encodeURIComponent(state.analysisId) + '?k=' + encodeURIComponent(state.accessKey), {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email, purpose: 'free_copy', marketing_consent: !!(marketing && marketing.checked),
          /* Mur des rêves (lot E) : consentement distinct, pseudo facultatif. */
          mur_consent: !!(murCase && murCase.checked),
          mur_pseudo: murCase && murCase.checked && murPseudo ? murPseudo.value.trim().slice(0, 24) : ''
        })
      }).then(function (r) { if (!r.ok) throw new Error('http_' + r.status); return r.json(); })
        .then(function (body) {
          state.email = email;
          copieConfirmee(email);
          var livraison = q('[data-dream-email]');
          if (livraison && !livraison.value) livraison.value = email;
          imageDeverrouiller(body && body.image ? body.image : null);
          track(config, 'dream_free_email_set', { analysis_id: state.analysisId, suites: !!(marketing && marketing.checked) });
          if (murCase && murCase.checked) track(config, 'dream_mur_proposed', { analysis_id: state.analysisId, pseudo: !!(murPseudo && murPseudo.value.trim()) });
        })
        .catch(function () {
          copieOccupe(false);
          copieErreur('L’envoi n’a pas abouti. Réessayez dans un instant.');
          track(config, 'dream_error', { reason: 'free_copy_failed' });
        });
    });

    /* Illustration offerte (lot D, maquette hybride) : demandée d'elle-même dès la lecture, dans
       le style par défaut. Sans adresse, l'API ne sert qu'une version floutée ; le voile et le
       cadenas sont dessinés ici, l'action est dans le champ e-mail dessous. Quotas et refus sur
       récit sensible vivent côté serveur ; 404 = illustration inactive, le module se retire. */
    var imageBloc = q('[data-dream-image]');
    var imageStyle = 'cuivre';
    function imageDire(texte, erreur) {
      var m = q('[data-dream-image-msg]');
      if (!m) return;
      m.textContent = texte || '';
      m.classList.toggle('is-erreur', !!erreur);
    }
    function imageEtat(nom) {
      if (!imageBloc) return;
      imageBloc.classList.remove('is-en-attente', 'is-verrouillee', 'is-illustree', 'is-indisponible');
      if (nom) imageBloc.classList.add(nom);
    }
    function captureLibelle(avecImage) {
      var lib = q('[data-dream-copie-libelle]');
      if (lib && !lib.dataset.dlLibelle) lib.textContent = avecImage ? 'Recevoir mon illustration et ma lecture' : 'Recevoir ma lecture';
    }
    function imageElements(free) {
      var hote = q('[data-dream-image-elements]');
      if (!hote) return;
      clear(hote);
      var noms = ((free && free.symbols) || []).filter(function (x) { return x && x.name; }).slice(0, 4).map(function (x) { return x.name; });
      if (!noms.length) return;
      hote.appendChild(document.createTextNode('Éléments utilisés : '));
      hote.appendChild(el('b', null, noms.join(' · ')));
    }
    function imageMontrer(url, origine, verrou) {
      var img = q('[data-dream-image-img]');
      if (!imageBloc || !img) return;
      img.src = url;
      img.alt = verrou ? 'Illustration masquée, à recevoir par e-mail' : 'Votre illustration, dessinée à partir de votre lecture';
      imageEtat(verrou ? 'is-verrouillee' : 'is-illustree');
      imageDire('');
      track(config, 'dream_image_view', { analysis_id: state.analysisId, origine: origine, verrou: !!verrou });
    }
    function imageDeverrouiller(url) {
      var img = q('[data-dream-image-img]');
      if (!imageBloc || !img || !imageBloc.classList.contains('is-verrouillee')) return;
      img.src = url || String(img.src).replace('v=floue', 'v=nette');
      img.alt = 'Votre illustration, dessinée à partir de votre lecture';
      imageEtat('is-illustree');
      track(config, 'dream_image_view', { analysis_id: state.analysisId, origine: 'deverrouillage', verrou: false });
    }
    function genererImage() {
      if (!imageBloc || !state.analysisId || imageBloc.dataset.dlEnCours === '1') return;
      imageBloc.dataset.dlEnCours = '1';
      imageEtat('is-en-attente');
      track(config, 'dream_image_request', { analysis_id: state.analysisId, style: imageStyle, auto: true });
      fetch(config.apiBase + '/dream/analysis/' + encodeURIComponent(state.analysisId) + '?k=' + encodeURIComponent(state.accessKey), {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ purpose: 'image', style: imageStyle })
      }).then(function (r) { return r.json().then(function (b) { return { ok: r.ok, status: r.status, body: b }; }, function () { return { ok: r.ok, status: r.status, body: null }; }); })
        .then(function (res) {
          delete imageBloc.dataset.dlEnCours;
          /* L'adresse a pu être posée pendant la génération : l'API sert alors l'image nette. */
          if (res.ok && res.body && res.body.image) { imageMontrer(res.body.image, 'generation', res.body.locked && !state.email); return; }
          if (res.status === 404) { hide(imageBloc); captureLibelle(false); return; }
          /* 422 (récit), 429 (quota), 503 (plafond du jour) : un message calme, pas une erreur. */
          var calme = res.status === 422 || res.status === 429 || res.status === 503;
          imageEtat('is-indisponible');
          imageDire(res.body && res.body.message ? res.body.message : 'L’illustration n’a pas abouti cette fois. Votre lecture reste entière.', !calme);
          captureLibelle(false);
          track(config, 'dream_error', { reason: 'image_' + res.status });
        })
        .catch(function () {
          delete imageBloc.dataset.dlEnCours;
          imageEtat('is-indisponible');
          imageDire('L’illustration n’a pas abouti cette fois. Votre lecture reste entière.', true);
          captureLibelle(false);
          track(config, 'dream_error', { reason: 'image_reseau' });
        });
    }
    function preparerImage(url, verrou, free) {
      if (!imageBloc) return;
      if (!config.imageEnabled) { hide(imageBloc); captureLibelle(false); return; }
      var img = q('[data-dream-image-img]');
      imageElements(free);
      if (img) img.removeAttribute('src');
      imageEtat(null);
      imageDire('');
      captureLibelle(true);
      show(imageBloc);
      if (url) imageMontrer(url, 'reprise', verrou);
      else genererImage();
    }

    /* paywall : ajout panier + checkout Shopify natif */
    /* Validation volontairement permissive : on refuse ce qui ne peut pas être une
       adresse, pas ce qui n'est pas dans une liste de domaines. */
    function emailPlausible(v) {
      return /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test(v) && v.length <= 254;
    }

    unlock.addEventListener('click', function () {
      if (!state.analysisId) return;

      /* Sans adresse, la commande serait encaissée puis indélivrable : on bloque ici
         plutôt que de laisser le checkout décider. */
      var champEmail = q('[data-dream-email]');
      var erreurEmail = q('[data-dream-email-erreur]');
      /* L'adresse vient normalement du champ de la lecture (maquette hybride : un seul champ).
         Si elle manque encore, le bloc de livraison s'ouvre ici, avant tout passage au panier. */
      if (champEmail && !champEmail.value.trim() && state.email) champEmail.value = state.email;
      var email = champEmail ? champEmail.value.trim() : '';
      if (champEmail) {
        if (!email) {
          show(q('[data-dream-livraison]'));
          hide(q('[data-dream-paywall-mail]'));
          champEmail.setAttribute('aria-invalid', 'true');
          if (erreurEmail) erreurEmail.textContent = 'Indiquez une adresse : l’analyse vous y sera envoyée.';
          champEmail.focus();
          track(config, 'dream_error', { reason: 'email_manquant', analysis_id: state.analysisId });
          return;
        }
        if (!emailPlausible(email)) {
          champEmail.setAttribute('aria-invalid', 'true');
          if (erreurEmail) erreurEmail.textContent = 'Cette adresse semble incomplète. Vérifiez-la.';
          champEmail.focus();
          track(config, 'dream_error', { reason: 'email_invalide', analysis_id: state.analysisId });
          return;
        }
        champEmail.removeAttribute('aria-invalid');
        if (erreurEmail) erreurEmail.textContent = '';
      }

      var items = [{
        id: Number(config.variantAnalysis), quantity: 1,
        properties: { _analysis_id: state.analysisId, _access_key: state.accessKey }
      }];
      var withBump = config.enableEbookBump && bump && bump.checked;
      if (withBump) items.push({ id: Number(config.variantEbook), quantity: 1 });

      track(config, 'dream_checkout_click', { analysis_id: state.analysisId,
        offer: withBump ? 'single_590_ebook' : 'single_590',
        /* Variante de style, pour lire l'effet du CTA doré sans toucher aux noms
           d'événements ni aux métriques historiques. */
        cta_variant: 'gold_v1' });
      if (withBump) track(config, 'dream_ebook_bump_accept', { analysis_id: state.analysisId });

      unlock.disabled = true;
      /* Le libellé vient du réglage de section (le prix y est interpolé) : on le mémorise
         plutôt que de le réécrire en dur, sinon une remise en état après échec afficherait
         un texte différent de celui que la page servait. */
      var libelleInitial = unlock.textContent;
      clear(unlock);
      unlock.appendChild(el('span', 'dl-spinner'));
      unlock.appendChild(document.createTextNode(' Redirection vers le paiement…'));

      /* L'adresse est enregistrée sur l'analyse AVANT le panier, et le panier n'est
         ouvert QUE si cet enregistrement a réellement abouti.

         La version précédente avalait l'échec — un `.catch` vide commenté « le
         checkout reste prioritaire » — et poursuivait. Une panne réseau, un 500 ou
         une réponse inattendue laissaient donc encaisser une commande que plus rien
         ne pouvait livrer. Ce chemin n'existait pas le 28/08 : il a été ajouté
         APRÈS, précisément pour empêcher ce cas — et il ne l'empêchait pas, faute
         de bloquer sur l'échec. Le paiement n'est jamais prioritaire sur la
         capacité à livrer ce qu'il achète.

         Aucune adresse ne sort d'ici : ni dans le message, ni dans l'événement. */
      enregistrerAdresse(config, state.analysisId, state.accessKey, email).then(function () {
      /* Écrit seulement maintenant : marquer « checkout » avant d'avoir la
         certitude de pouvoir livrer ferait attendre un paiement qui n'aura pas lieu. */
      store({ analysis_id: state.analysisId, access_key: state.accessKey, status: 'checkout', ts: Date.now() });
      fetch('/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) { if (!r.ok) throw new Error('cart'); return r.json(); })
        .then(function () {
          /* La reprise est en localStorage : revenir sur cette page suffit à
             retrouver l'analyse. Encore faut-il le savoir — Shopify termine sur
             sa page de remerciement, sans retour. */
          message('info', 'Après le paiement, revenez sur cette page : votre analyse s’y affichera. Elle vous est aussi envoyée par e-mail.');
          /* Panier accepté : la redirection part. Sans cet événement, un clic sans intention
             enregistrée et un panier qui échoue se confondent avec un abandon au paiement. */
          track(config, 'dream_checkout_redirect', { analysis_id: state.analysisId });
          window.location.href = config.checkoutUrl;
        })
        .catch(function () {
          unlock.disabled = false;
          unlock.textContent = libelleInitial;
          message('error', 'Le panier n’a pas répondu. Réessayez dans un instant.');
          track(config, 'dream_error', { reason: 'cart_add_failed' });
        });
      }, function () {
        /* On reste sur le paywall. Le bouton reprend son libellé d'origine et
           redevient cliquable : l'échec doit être réessayable, pas terminal. */
        unlock.disabled = false;
        unlock.textContent = libelleInitial;
        message('error', 'Nous n’avons pas pu enregistrer votre adresse. Vérifiez votre connexion et réessayez.');
        track(config, 'dream_error', { reason: 'email_patch_failed' });
      });
    });

    /* Reprise depuis le lien de l'e-mail de lecture (lot B) : ?a=…&k=… affiche la lecture
       gratuite et son paywall sans rien redemander, ou l'analyse complète si elle est payée.
       La session locale est reposée pour que le paiement fonctionne sur cet appareil. */
    function reprendreDepuisLien() {
      var params = new URLSearchParams(location.search);
      var a = params.get('a'), k = params.get('k');
      var origine = 'lien';
      if (a && k) {
        try { history.replaceState(null, '', location.pathname); } catch (e) {}
      } else {
        /* Rechargement (08/09/2026) : une lecture gratuite obtenue sur cet appareil dans les
           dernières 24 h se réaffiche telle quelle, avec son image et son offre, au lieu de
           renvoyer au formulaire vide. Même chemin que le lien de l'e-mail, même API. */
        var session = restore();
        if (!session || session.status !== 'free' || !session.analysis_id || !session.access_key) return false;
        if (!session.ts || Date.now() - session.ts > 24 * 3600 * 1000) return false;
        a = session.analysis_id; k = session.access_key; origine = 'session';
        /* Au rechargement, le navigateur remet l'ancienne position de défilement après coup et
           annule le défilement vers le résultat : on lui retire cette reprise automatique. */
        try { if ('scrollRestoration' in history) history.scrollRestoration = 'manual'; } catch (e) {}
      }
      show(resultsSection); show(freeSkeleton); hide(paywall);
      fetch(config.apiBase + '/dream/analysis/' + encodeURIComponent(a) + '?k=' + encodeURIComponent(k))
        .then(function (r) { return r.ok ? r.json() : Promise.reject(new Error('http_' + r.status)); })
        .then(function (body) {
          hide(freeSkeleton);
          if (body.paid_result) {
            hide(resultsSection); show(fullSection);
            store({ analysis_id: a, access_key: k, status: 'paid', ts: Date.now() });
            renderFull(fullTarget, body.paid_result, config, { niveau: 'h2', free: body.free_result || null, image: body.image_hd || body.image || null, actions: q('[data-dream-actions]') });
            return;
          }
          if (!body.free_result) throw new Error('vide');
          state.analysisId = a; state.accessKey = k;
          store({ analysis_id: a, access_key: k, status: 'free', ts: Date.now() });
          renderFree(freeTarget, body.free_result);
          renderLocked(q, body.free_result);
          renderInventaire(q, body.free_result, 'symbolique', config);
          preparerImage(body.image || null, !!body.image_locked, body.free_result);
          copieConfirmee(null);
          show(paywall);
          track(config, 'dream_free_result_view', { analysis_id: a, origine: origine });
          observerPaywall(config, paywall, a);
          allerAuResultat(resultsSection);
        })
        .catch(function () {
          hide(freeSkeleton); hide(resultsSection);
          message('error', 'Ce lien ne permet plus d’ouvrir la lecture. Racontez votre rêve à nouveau, c’est gratuit.');
        });
      return true;
    }
    if (reprendreDepuisLien()) return;

    /* reprise : retour sur la page après paiement */
    var saved = restore();
    if (saved && saved.analysis_id && saved.status === 'checkout') {
      show(resultsSection);
      show(freeSkeleton);
      pollUntilPaid(config, saved, function (paid) {
        hide(freeSkeleton);
        hide(paywall);
        hide(resultsSection);
        show(fullSection);
        renderFull(fullTarget, paid, config, { niveau: 'h2', actions: q('[data-dream-actions]') });
        store({ analysis_id: saved.analysis_id, access_key: saved.access_key, status: 'paid', ts: Date.now() });
        track(config, 'dream_full_result_generated', { analysis_id: saved.analysis_id });
        fullSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, function () {
        hide(freeSkeleton);
        hide(resultsSection);
      });
    }
  }

  /* ---------- polling état payé ---------- */

  function pollUntilPaid(config, session, onPaid, onGiveUp) {
    var started = Date.now();
    (function tick() {
      fetch(config.apiBase + '/dream/analysis/' + encodeURIComponent(session.analysis_id) +
        '?k=' + encodeURIComponent(session.access_key))
        .then(function (r) { return r.json(); })
        .then(function (body) {
          if (body && body.paid_result) return onPaid(body.paid_result);
          if (Date.now() - started > config.pollTimeoutMs) return onGiveUp();
          setTimeout(tick, config.pollIntervalMs);
        })
        .catch(function () {
          if (Date.now() - started > config.pollTimeoutMs) return onGiveUp();
          setTimeout(tick, config.pollIntervalMs);
        });
    })();
  }

  /* ---------- page résultat ---------- */

  function initResult(root) {
    var config = cfg(root);
    var target = root.querySelector('[data-dream-full]');
    var skeleton = root.querySelector('[data-dream-skeleton]');
    var msg = root.querySelector('[data-dream-form-msg]');
    var params = new URLSearchParams(location.search);
    var id = params.get('a');
    var key = params.get('k');
    var token = params.get('token');

    if (!id && !token) {
      var saved = restore();
      if (saved) { id = saved.analysis_id; key = saved.access_key; }
    }
    if (!id && !token) {
      hide(skeleton);
      msg.className = 'dl-msg dl-msg--info';
      msg.textContent = 'Lien incomplet. Ouvrez le lien reçu par e-mail pour retrouver votre analyse.';
      show(msg);
      return;
    }

    var url = token
      ? config.apiBase + '/dream/result/' + encodeURIComponent(token)
      : config.apiBase + '/dream/analysis/' + encodeURIComponent(id) + '?k=' + encodeURIComponent(key);

    function attempt(retries) {
      fetch(url).then(function (r) {
        return r.json().then(function (b) { return { status: r.status, body: b }; }, function () { return { status: r.status, body: null }; });
      }).then(function (res) {
        var body = res.body;
        /* Lien privé arrivé à échéance : l'API répond 410, la page montre l'état dédié. */
        if (res.status === 410) {
          hide(skeleton); hide(msg);
          show(root.querySelector('[data-dream-expired]'));
          return;
        }
        /* Jeton inconnu : ce n'est pas une génération en cours, inutile de laisser attendre. */
        if (res.status === 404) {
          hide(skeleton);
          msg.className = 'dl-msg dl-msg--error';
          msg.textContent = 'Analyse introuvable ou lien expiré. Les analyses restent accessibles 90 jours.';
          show(msg);
          return;
        }
        if (body && body.paid_result) {
          hide(skeleton); hide(msg);
          /* Options de rendu posées sur la config : titre en h1, thème dominant si l'API
             le renvoie, actions du gabarit déplacées dans le pied du rapport. */
          config.rendu = { niveau: 'h1', free: body.free_result || null, image: body.image_hd || body.image || null, actions: root.querySelector('[data-dream-actions]') };
          renderFull(target, body.paid_result, config);
          track(config, 'dream_full_result_view', { analysis_id: id });
          /* `id` est nul sur le chemin e-mail (lien ?token=…) : on prend l'identifiant
             que l'API vient de renvoyer, sinon la mesure perdrait justement le parcours
             le plus fréquent. */
          suivreAutreReve(root, config, body.analysis_id || id || null);
          return;
        }
        if (body && body.status && body.status !== 'paid' && retries > 0) {
          return setTimeout(function () { attempt(retries - 1); }, config.pollIntervalMs);
        }
        hide(skeleton);
        msg.className = 'dl-msg dl-msg--info';
        msg.textContent = 'Votre analyse est encore en cours de génération. Cette page se met à jour automatiquement, ou consultez l’e-mail que nous venons de vous envoyer.';
        show(msg);
      }).catch(function () {
        hide(skeleton);
        msg.className = 'dl-msg dl-msg--error';
        msg.textContent = 'Analyse introuvable ou lien expiré. Les analyses restent accessibles 90 jours.';
        show(msg);
      });
    }
    attempt(20);
  }

  /* ---------- CTA article ---------- */

  /**
   * Une section ajoutée au modèle d'article se place forcément après la section qui rend
   * l'article entier. Pour remonter le CTA dans le corps du texte sans modifier
   * main-article.liquid — donc sans risquer de casser le rendu de tous les articles —
   * on le déplace côté navigateur, après le Nième paragraphe.
   *
   * Si le conteneur d'article est introuvable, le bloc reste où il est : dégradation
   * silencieuse plutôt que disparition.
   */
  function moveIntoArticle(node) {
    // Les attributs sont portés par le conteneur de la section, pas par le bloc CTA :
    // c'est ce conteneur qu'il faut déplacer, sinon on sortirait le CTA de son enveloppe.
    var host = node.closest ? node.closest('[data-dl-move-into]') : null;
    if (!host) return;
    var selector = host.getAttribute('data-dl-move-into');
    if (!selector) return;
    var after = parseInt(host.getAttribute('data-dl-move-after') || '1', 10);
    var ratio = parseInt(host.getAttribute('data-dl-move-ratio') || '0', 10);

    var container = null;
    var candidates = selector.split(',');
    for (var i = 0; i < candidates.length && !container; i++) {
      container = document.querySelector(candidates[i].trim());
    }
    if (!container || container.contains(host)) return;

    var paragraphs = container.querySelectorAll(':scope > p, :scope > div > p');
    if (paragraphs.length === 0) return;

    // En proportion quand un ratio est donné : le même pourcentage d'un article de
    // 14 paragraphes et d'un autre de 76 tombe au même endroit relatif.
    var rang = ratio > 0
      ? Math.max(1, Math.round(paragraphs.length * ratio / 100))
      : Math.min(after, paragraphs.length);
    var anchor = paragraphs[Math.min(rang, paragraphs.length) - 1];
    if (!anchor || !anchor.parentNode) return;

    /* On insère après le BLOC DE PREMIER NIVEAU qui contient le paragraphe visé,
       jamais après le paragraphe lui-même. Les articles portent des encarts
       (`div.dl-snippet-reponse`, bordure violette, fond clair) dont les
       paragraphes sont comptés — c'est voulu, ils font partie du texte — mais
       insérer juste après l'un d'eux plaçait le CTA À L'INTÉRIEUR de l'encart :
       une carte d'offre enfermée dans une boîte de réponse, avec la marge et la
       bordure de celle-ci. Vu le 03/09/2026 sur « Rêver de grossesse », le CTA
       coincé sous le libellé « Rêver de grossesse : signification ».
       Le rang « après N paragraphes » garde exactement le même sens ; seul le
       point d'insertion remonte à la frontière du bloc. */
    var bloc = anchor;
    while (bloc.parentNode && bloc.parentNode !== container) bloc = bloc.parentNode;
    if (bloc.parentNode !== container) return;
    container.insertBefore(host, bloc.nextSibling);
  }

  /**
   * Volet bas. Pas une modale : au chargement il est hors écran et invisible, rien
   * ne couvre le texte. Il apparaît une fois qu'une part de l'article est lue —
   * le réglage de la section, 28 % par défaut, parce que les lecteurs ne vont pas
   * loin — une seule fois par session. Refermé, il se tait sept jours.
   *
   * La fermeture est mesurée (`dream_cta_dismiss`) : c'est le signal de gêne. Un
   * volet qui gagne des clics mais se fait fermer massivement n'est pas un gain.
   */
  var SHEET_FERME_KEY = 'dl_sheet_ferme';
  var SHEET_VU_KEY = 'dl_sheet_vu';
  var SHEET_SILENCE_MS = 7 * 24 * 3600 * 1000;

  function initSheet(node, host, config, props) {
    try {
      var ferme = parseInt(localStorage.getItem(SHEET_FERME_KEY) || '0', 10);
      if (ferme && Date.now() - ferme < SHEET_SILENCE_MS) return;
      if (sessionStorage.getItem(SHEET_VU_KEY) === '1') return;
    } catch (e) { /* stockage indisponible : on affiche, sans mémoire */ }

    var ratio = parseInt(host.getAttribute('data-dl-sheet-ratio') || '28', 10);
    var container = document.querySelector('.article-template__content, .article__content, .rte');
    if (!container) return;

    var montre = false;
    function montrer() {
      if (montre) return;
      montre = true;
      node.classList.add('dl-sheet--visible');
      try { sessionStorage.setItem(SHEET_VU_KEY, '1'); } catch (e) { /* ignoré */ }
      window.removeEventListener('scroll', surDefilement);
    }
    function surDefilement() {
      var r = container.getBoundingClientRect();
      var lu = (window.innerHeight - r.top) / r.height;   // part du corps passée sous le haut de l'écran
      if (lu >= ratio / 100) montrer();
    }
    window.addEventListener('scroll', surDefilement, { passive: true });
    surDefilement();

    var fermer = node.querySelector('[data-dl-sheet-fermer]');
    if (fermer) fermer.addEventListener('click', function () {
      node.classList.remove('dl-sheet--visible');
      try { localStorage.setItem(SHEET_FERME_KEY, String(Date.now())); } catch (e) { /* ignoré */ }
      track(config, 'dream_cta_dismiss', props);
    });
  }

  function initCta(node) {
    var sheetHost = node.closest ? node.closest('[data-dl-sheet]') : null;
    if (!sheetHost) moveIntoArticle(node);

    // Une page article ne porte pas le bloc de config de la section outil :
    // le snippet fournit l'URL d'API en attribut pour que le miroir serveur fonctionne.
    var config = cfg(document);
    var apiBase = node.getAttribute('data-api-base');
    if (apiBase) config.apiBase = apiBase;
    var props = {
      article_handle: node.getAttribute('data-article') || null,
      dream_topic: node.getAttribute('data-topic') || null,
      cta_position: node.getAttribute('data-position') || null,
      source_url: location.href
    };

    /* Test de copie des CTA d'articles (03/09/2026). Le snippet rend LES DEUX
       copies — générique et question spécifique au symbole — toutes deux
       masquées par la CSS ; on n'en révèle qu'une. Le bras est tiré une fois par
       navigateur et conservé, pour que le même visiteur voie toujours la même
       carte ; il part avec la vue et le clic (cta_variant) et suit le lien
       (?dl_v=) jusqu'au formulaire. Sans copie double dans le bloc — les autres
       formats, le volet — rien ne change. */
    var copies = node.querySelectorAll('[data-dl-copie]');
    if (copies.length) {
      var brasCta = null;
      try {
        brasCta = localStorage.getItem('dl_ab_cta');
        if (brasCta !== 'generique_v1' && brasCta !== 'question_v1') {
          brasCta = Math.random() < 0.5 ? 'generique_v1' : 'question_v1';
          localStorage.setItem('dl_ab_cta', brasCta);
        }
      } catch (e) { brasCta = 'generique_v1'; /* pas de stockage : contrôle, sans mémoire */ }
      var cle = brasCta === 'question_v1' ? 'question' : 'generique';
      Array.prototype.forEach.call(copies, function (c) {
        c.hidden = c.getAttribute('data-dl-copie') !== cle;
      });
      props.cta_variant = brasCta;
    }

    if (sheetHost) initSheet(node, sheetHost, config, props);
    if (props.cta_variant) {
      Array.prototype.forEach.call(node.querySelectorAll('a.dl-btn'), function (a) {
        var h = a.getAttribute('href') || '';
        if (h && h.indexOf('dl_v=') === -1) {
          a.setAttribute('href', h + (h.indexOf('?') === -1 ? '?' : '&') + 'dl_v=' + props.cta_variant);
        }
      });
    }
    Array.prototype.forEach.call(node.querySelectorAll('a.dl-btn'), function (a) {
      a.addEventListener('click', function () { track(config, 'dream_cta_click', props); });
    });

    if (!('IntersectionObserver' in window)) { track(config, 'dream_cta_view', props); return; }
    var seen = false;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting && !seen) { seen = true; track(config, 'dream_cta_view', props); io.disconnect(); }
      });
    }, { threshold: 0.5 });
    io.observe(node);
  }

  /* ---------- amorçage ---------- */

  /* La garde de ré-entrée suffit contre le double chargement, mais l'éditeur de thème
     recharge une section sans recharger la page : on marque aussi chaque nœud. */
  function unefois(init) {
    return function (node) {
      if (node.getAttribute('data-dl-monte') === '1') return;
      node.setAttribute('data-dl-monte', '1');
      init(node);
    };
  }

  /**
   * Enregistre l'adresse sur l'analyse, AVANT le panier. Rejette dès que
   * l'enregistrement n'est pas certain : panne réseau, délai dépassé, statut non
   * 2xx, ou corps de réponse qui ne confirme rien.
   *
   * La version précédente avalait l'échec — un `.catch` vide commenté « le
   * checkout reste prioritaire » — et poursuivait vers le paiement. Une panne
   * laissait donc encaisser une commande que plus rien ne pouvait livrer. Cette
   * collecte a été ajoutée APRÈS le 28/08 pour empêcher ce cas ; sans blocage sur
   * l'échec, elle ne l'empêchait pas. Le paiement n'est jamais prioritaire sur la
   * capacité à livrer ce qu'il achète.
   *
   * Hors du gestionnaire de clic, et paramétrée, pour être exécutable en test :
   * cette mécanique-là ne peut pas se vérifier en relisant le fichier.
   *
   * Aucune adresse ne sort d'ici : ni message, ni journal, ni événement.
   */
  function enregistrerAdresse(config, analysisId, accessKey, email, delaiMs) {
    if (!email) return Promise.reject(new Error('sans_adresse'));
    var abandon = typeof AbortController === 'function' ? new AbortController() : null;
    /* Sans délai maximal, une requête suspendue laisse le bouton en attente
       indéfiniment : ni paiement, ni message, ni moyen de réessayer. */
    var minuteur = abandon
      ? setTimeout(function () { abandon.abort(); }, delaiMs || 10000)
      : null;
    var options = {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email })
    };
    if (abandon) options.signal = abandon.signal;
    return fetch(config.apiBase + '/dream/analysis/' + encodeURIComponent(analysisId) +
                 '?k=' + encodeURIComponent(accessKey), options)
      .then(function (r) {
        if (minuteur) clearTimeout(minuteur);
        if (!r.ok) throw new Error('http_' + r.status);
        /* Un 2xx au corps illisible ne prouve pas l'enregistrement. */
        return r.json().then(function (o) {
          if (!o || o.ok !== true) throw new Error('reponse_invalide');
        }, function () { throw new Error('reponse_invalide'); });
      }, function (e) {
        if (minuteur) clearTimeout(minuteur);
        throw e;
      });
  }

  function boot() {
    document.querySelectorAll('[data-dream-analyzer]').forEach(unefois(initTool));
    document.querySelectorAll('[data-dream-result-page]').forEach(unefois(initResult));
    document.querySelectorAll('[data-dream-cta]').forEach(unefois(initCta));
  }

  /* Exposé pour les tests : le verrou de checkout se vérifie en l'exécutant. */
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { enregistrerAdresse: enregistrerAdresse };
  }
  if (typeof document === 'undefined') return;

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.DreamAnalyzer = { boot: boot, track: track };
})();
